'use client'

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Car, CarInput } from '@garage/shared'
import type { FuelType } from '@garage/shared'
import { carInputSchema, FUEL_TYPES, FUEL_TYPE_LABELS } from '@garage/shared'
import {
  Dialog,
  TextField,
  Select,
  Button,
  useSnackbar,
  type SelectOption,
} from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'

interface CarFormDialogProps {
  open: boolean
  car?: Car
  clientId: string | null
  onClose: () => void
}

interface FormState {
  brand: string
  model: string
  plate: string
  year: string
  vin: string
  color: string
  engineSize: string
  fuelType: string
}

// Convert empty strings to null for optional text fields
const emptyToNull = (value: string): string | null => (value === '' ? null : value)

export function CarFormDialog({
  open,
  car,
  clientId,
  onClose,
}: CarFormDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const isCreate = !car

  const [formState, setFormState] = useState<FormState>({
    brand: car?.brand ?? '',
    model: car?.model ?? '',
    plate: car?.plate ?? '',
    year: car?.year ? String(car.year) : '',
    vin: car?.vin ?? '',
    color: car?.color ?? '',
    engineSize: car?.engineSize ?? '',
    fuelType: car?.fuelType ?? '',
  })

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormState({
        brand: car?.brand ?? '',
        model: car?.model ?? '',
        plate: car?.plate ?? '',
        year: car?.year ? String(car.year) : '',
        vin: car?.vin ?? '',
        color: car?.color ?? '',
        engineSize: car?.engineSize ?? '',
        fuelType: car?.fuelType ?? '',
      })
      setFieldErrors({})
    }
  }, [open, car])

  const mutation = useMutation({
    mutationFn: async (input: CarInput) => {
      if (isCreate) {
        return api.cars.create(input)
      } else {
        return api.cars.update(car!.id, input)
      }
    },
    onSuccess: () => {
      if (clientId) {
        queryClient.invalidateQueries({ queryKey: qk.client(clientId) })
      }
      queryClient.invalidateQueries({ queryKey: qk.cars() })
      queryClient.invalidateQueries({ queryKey: qk.dashboard })
      snackbar.success(
        isCreate ? t('carForm.carAddedSuccessfully') : t('carForm.carUpdatedSuccessfully')
      )
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        // VIN duplicate conflict (409) surfaces as a field error
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const yearNum = formState.year === '' ? null : parseInt(formState.year, 10)

    const input: CarInput = {
      clientId,
      brand: formState.brand,
      model: formState.model,
      plate: formState.plate,
      year: yearNum,
      vin: emptyToNull(formState.vin),
      color: emptyToNull(formState.color),
      engineSize: emptyToNull(formState.engineSize),
      // The select only offers FUEL_TYPES, so this is a real narrowing rather
      // than an `as any` that would hide a bad value from the compiler.
      fuelType: formState.fuelType === '' ? null : (formState.fuelType as FuelType),
    }

    const result = carInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        const key = issue.path.join('.')
        errors[key] = issue.message
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    mutation.mutate(input)
  }

  const fuelTypeOptions: SelectOption[] = [
    { value: '', label: t('carForm.selectFuelType') },
    ...FUEL_TYPES.map((type) => ({
      value: type,
      label: t(`carForm.fuelTypes.${type}`, FUEL_TYPE_LABELS[type]),
    })),
  ]

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('carForm.addCar') : t('carForm.editCar')}
      size="md"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="filled"
            onClick={handleSubmit}
            loading={mutation.isPending}
          >
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            label={t('carForm.brand')}
            value={formState.brand}
            onChange={(v) => setFormState((s) => ({ ...s, brand: v }))}
            error={fieldErrors.brand}
            fullWidth
            required
          />
          <TextField
            label={t('carForm.model')}
            value={formState.model}
            onChange={(v) => setFormState((s) => ({ ...s, model: v }))}
            error={fieldErrors.model}
            fullWidth
            required
          />
        </div>

        <TextField
          label={t('carForm.plate')}
          value={formState.plate}
          onChange={(v) => setFormState((s) => ({ ...s, plate: v }))}
          error={fieldErrors.plate}
          fullWidth
          required
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            label={t('carForm.year')}
            value={formState.year}
            onChange={(v) => setFormState((s) => ({ ...s, year: v }))}
            error={fieldErrors.year}
            integer
            fullWidth
          />
          <TextField
            label={t('carForm.vin')}
            value={formState.vin}
            onChange={(v) => setFormState((s) => ({ ...s, vin: v }))}
            error={fieldErrors.vin}
            fullWidth
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            label={t('carForm.colour')}
            value={formState.color}
            onChange={(v) => setFormState((s) => ({ ...s, color: v }))}
            error={fieldErrors.color}
            fullWidth
          />
          <TextField
            label={t('carForm.engineSize')}
            value={formState.engineSize}
            onChange={(v) => setFormState((s) => ({ ...s, engineSize: v }))}
            error={fieldErrors.engineSize}
            fullWidth
            placeholder={t('carForm.engineSizePlaceholder')}
          />
        </div>

        <Select
          label={t('carForm.fuelType')}
          value={formState.fuelType}
          onChange={(v) => setFormState((s) => ({ ...s, fuelType: v }))}
          options={fuelTypeOptions}
          error={fieldErrors.fuelType}
          fullWidth
        />
      </form>
    </Dialog>
  )
}
